import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import { initialDockWidth } from "../geometry/dock-default-width"
import { Types as HistoryTypes } from "../../../../actions/app/history"

/** `full` promotes the same dock node to cover the stage; it is a view state, never a route. */
export type DockView = "closed" | "docked" | "full"

/** Which body the chat surface shows. The conversations panel stays either way — this is not a route. */
export type ChatSurfaceView = "conversation" | "artifacts" | "workflows"

export type ArtifactFilter = "all" | "yours" | "shared" | "scheduled"

export type DockPane = "chat" | "skills" | "admin_settings"

export interface ComposerSeed {
  text: string
  /** Epoch ms at dispatch; lets the consumer reject seeds stranded by a navigation that never mounted it. */
  seededAt: number
}

export interface AgentChatState {
  /** Whether the Kelly AI conversations side panel is collapsed to the top-bar "Chats" dock affordance. */
  panelCollapsed: boolean
  sectionCollapsed: Record<"pinned" | "recents" | "reports", boolean>
  thinkingTrailExpandedDefaultForUnseenMessagesByConversationId: Record<string, boolean>
  thinkingTrailExpandedFrozenByMessageId: Record<string, boolean>
  thinkingTrailExpandedOwnedByActiveStreamByConversationId: Record<string, boolean>
  /** Prompt to pre-fill the Kelly composer with; set by the nav nudge, consumed once by KellyChatProvider. */
  pendingComposerSeed: ComposerSeed | null
  /**
   * Sticky once Kelly has been shown in this account session. Gates two things that now flip on the same
   * event: the hoisted controller's thread-list query and the dock's transcript surface, which
   * mounts on first show and never unmounts. An account that never opens Kelly pays no `ChatBox` per page.
   */
  hasActivated: boolean
  /** Which view the single app-level dock is in. */
  dockView: DockView
  dockPane: DockPane
  /** The user's committed dock width. Nothing re-clamps it: `dockWidthCss` re-resolves the bounds at paint time. */
  dockWidthPx: number
  /**
   * Conversation transcript, the Artifacts list or the Workflows list. In the store rather than in `ConversationSurface`
   * because the dock's conversations flyout renders the nav item as a sibling of the body it switches.
   */
  surfaceView: ChatSurfaceView
  reportToOpen: string | null
  artifactsFilterToOpen: ArtifactFilter | null
  workflowToOpen: string | null
}

export const AGENT_CHAT_INITIAL_STATE: AgentChatState = {
  panelCollapsed: false,
  sectionCollapsed: { pinned: false, recents: false, reports: false },
  thinkingTrailExpandedDefaultForUnseenMessagesByConversationId: {},
  thinkingTrailExpandedFrozenByMessageId: {},
  thinkingTrailExpandedOwnedByActiveStreamByConversationId: {},
  pendingComposerSeed: null,
  hasActivated: false,
  dockView: "closed",
  dockPane: "chat",
  dockWidthPx: initialDockWidth(),
  surfaceView: "conversation",
  reportToOpen: null,
  artifactsFilterToOpen: null,
  workflowToOpen: null,
}

export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState: AGENT_CHAT_INITIAL_STATE,
  reducers: {
    collapsePanel(state) {
      state.panelCollapsed = true
    },
    expandPanel(state) {
      state.panelCollapsed = false
    },
    setSectionCollapsed(state, action: PayloadAction<{ section: keyof AgentChatState["sectionCollapsed"], collapsed: boolean }>) {
      state.sectionCollapsed[action.payload.section] = action.payload.collapsed
    },
    seedThinkingTrailIfUnseen(state, action: PayloadAction<{ conversationId: string, messageId: string }>) {
      const { conversationId, messageId } = action.payload
      if (state.thinkingTrailExpandedFrozenByMessageId[messageId] === undefined) {
        state.thinkingTrailExpandedFrozenByMessageId[messageId] =
          state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[conversationId]
            ?? state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[conversationId]
            ?? false
        Reflect.deleteProperty(state.thinkingTrailExpandedOwnedByActiveStreamByConversationId, conversationId)
      }
    },
    seedActiveStreamThinkingTrail(state, action: PayloadAction<{ conversationId: string }>) {
      const { conversationId } = action.payload
      if (state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[conversationId] === undefined) {
        state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[conversationId] =
          state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[conversationId] ?? false
      }
    },
    setThinkingTrailExpanded(state, action: PayloadAction<{ conversationId: string, messageId: string, expanded: boolean }>) {
      state.thinkingTrailExpandedFrozenByMessageId[action.payload.messageId] = action.payload.expanded
      state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[action.payload.conversationId] = action.payload.expanded
    },
    setThinkingTrailExpandedDefault(state, action: PayloadAction<{ conversationId: string, expanded: boolean }>) {
      const { conversationId, expanded } = action.payload
      state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[conversationId] = expanded
      state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[conversationId] = expanded
    },
    /**
     * A new chat's rolling default and active-stream ownership are keyed under `PENDING_NEW_THREAD_KEY`
     * until the thread adopts a server id — re-key them onto the real id so a sibling toggle made before
     * adoption survives it, instead of the adopted id starting with no default.
     */
    adoptThinkingTrailConversation(state, action: PayloadAction<{ from: string, to: string }>) {
      const { from, to } = action.payload
      if (state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[from] !== undefined) {
        state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[to] =
          state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[from]
        Reflect.deleteProperty(state.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId, from)
      }
      if (state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[from] !== undefined) {
        state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[to] =
          state.thinkingTrailExpandedOwnedByActiveStreamByConversationId[from]
        Reflect.deleteProperty(state.thinkingTrailExpandedOwnedByActiveStreamByConversationId, from)
      }
    },
    seedComposer: {
      reducer(state, action: PayloadAction<ComposerSeed>) {
        state.pendingComposerSeed = action.payload
      },
      prepare(text: string) {
        return { payload: { text, seededAt: Date.now() } }
      },
    },
    consumeComposerSeed(state) {
      state.pendingComposerSeed = null
    },
    /**
     * The only path that changes which view the dock is in — every control, launcher and the `/kelly-ai`
     * bridge goes through it, so there is one place where showing Kelly commits the sticky activation.
     * Activation stays sticky rather than tracking visibility: an un-sticky predicate would disable the
     * thread-list query on close, and React Query would refetch — and re-run the draft prune — on reopen.
     */
    setDockView(state, action: PayloadAction<DockView>) {
      state.dockView = action.payload
      if (action.payload !== "closed") state.hasActivated = true
      else state.dockPane = "chat"
    },
    openSkillsPane(state) {
      state.dockPane = "skills"
    },
    openAdminSettingsPane(state) {
      state.dockPane = "admin_settings"
    },
    closeDockPane(state) {
      state.dockPane = "chat"
    },
    setDockWidth(state, action: PayloadAction<number>) {
      state.dockWidthPx = action.payload
    },
    setSurfaceView(state, action: PayloadAction<ChatSurfaceView>) {
      state.surfaceView = action.payload
    },
    setReportToOpen(state, action: PayloadAction<string | null>) {
      state.reportToOpen = action.payload
    },
    setArtifactsFilterToOpen(state, action: PayloadAction<ArtifactFilter | null>) {
      state.artifactsFilterToOpen = action.payload
    },
    setWorkflowToOpen(state, action: PayloadAction<string | null>) {
      state.workflowToOpen = action.payload
    },
  },
  extraReducers(builder) {
    // Collapse is a per-session layout pref that persists across routes but must not leak across accounts —
    // reset it on account switch so a new account starts expanded (tabs are already route-scoped).
    builder.addCase(HistoryTypes.SELECT_ACCOUNT, () => AGENT_CHAT_INITIAL_STATE)
  },
})

export const { collapsePanel, expandPanel, setSectionCollapsed, seedThinkingTrailIfUnseen, seedActiveStreamThinkingTrail, setThinkingTrailExpanded, setThinkingTrailExpandedDefault, adoptThinkingTrailConversation, seedComposer, consumeComposerSeed, setDockView, setDockWidth, setSurfaceView, setReportToOpen, setArtifactsFilterToOpen, setWorkflowToOpen, openSkillsPane, openAdminSettingsPane, closeDockPane } = agentChatSlice.actions

export const selectPanelCollapsed = (state: ReduxState): boolean => state.agentChat.panelCollapsed

export const selectSectionCollapsed = (state: ReduxState, section: keyof AgentChatState["sectionCollapsed"]): boolean => state.agentChat.sectionCollapsed[section]

export const selectThinkingTrailExpanded = (state: ReduxState, conversationId: string, messageId: string, streaming: boolean): boolean =>
  streaming
    ? state.agentChat.thinkingTrailExpandedOwnedByActiveStreamByConversationId[conversationId]
      ?? state.agentChat.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[conversationId]
      ?? false
    : state.agentChat.thinkingTrailExpandedFrozenByMessageId[messageId]
      ?? state.agentChat.thinkingTrailExpandedOwnedByActiveStreamByConversationId[conversationId]
      ?? state.agentChat.thinkingTrailExpandedDefaultForUnseenMessagesByConversationId[conversationId]
      ?? false

export const selectPendingComposerSeed = (state: ReduxState): ComposerSeed | null => state.agentChat.pendingComposerSeed

export const selectHasActivated = (state: ReduxState): boolean => state.agentChat.hasActivated

export const selectDockView = (state: ReduxState): DockView => state.agentChat.dockView

export const selectDockPane = (state: ReduxState): DockPane => state.agentChat.dockPane

export const selectDockWidthPx = (state: ReduxState): number => state.agentChat.dockWidthPx

export const selectSurfaceView = (state: ReduxState): ChatSurfaceView => state.agentChat.surfaceView

export const selectReportToOpen = (state: ReduxState): string | null => state.agentChat.reportToOpen
export const selectArtifactsFilterToOpen = (state: ReduxState): ArtifactFilter | null => state.agentChat.artifactsFilterToOpen
export const selectWorkflowToOpen = (state: ReduxState): string | null => state.agentChat.workflowToOpen

export default agentChatSlice.reducer
